'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

// Client-side currency conversion for price tables (eSIM ledger / quiz / cards).
//
// The native price a partner charges (USD or EUR, sometimes RUB) stays the
// primary number — we never replace it. When the chosen "display" currency
// differs from a price's native currency, <ConvMoney> adds a second line
// with the converted amount. Default display: the country's base currency
// (EUR for euro countries, otherwise USD) on the EN site, RUB on the RU site
// — RU visitors can switch to a CIS currency and the choice is remembered.
//
// Rates come from the existing /api/currency-rates proxy (base USD), so one
// USD-anchored map converts between any pair: amount * rate[to] / rate[from].

const SYM = { USD: '$', EUR: '€', RUB: '₽', UAH: '₴', KZT: '₸', BYN: 'Br ', GBP: '£' }
export const curSym = (c) => SYM[c] || (c ? c + ' ' : '')

// Currencies shown without decimals (large magnitude vs USD/EUR).
const NO_DECIMALS = new Set(['RUB', 'KZT', 'UAH'])

const CUR_NAME = {
    ru: { USD: 'Доллар', EUR: 'Евро', RUB: 'Рубль', UAH: 'Гривна', KZT: 'Тенге', BYN: 'Бел. рубль', GBP: 'Фунт' },
    en: { USD: 'Dollar', EUR: 'Euro', RUB: 'Ruble', UAH: 'Hryvnia', KZT: 'Tenge', BYN: 'Belarus ruble', GBP: 'Pound' },
}

// Switcher options per locale. RU leads with CIS currencies; EN leads with the
// country base currency (prepended in the provider when not already present).
const RU_ORDER = ['RUB', 'UAH', 'KZT', 'BYN', 'USD', 'EUR']
const EN_ORDER = ['GBP', 'USD', 'EUR']

// Everything we ever need to convert between, anchored to USD.
const FETCH_SYMBOLS = ['EUR', 'RUB', 'UAH', 'KZT', 'BYN', 'GBP']
const STORAGE_KEY = 'tlh.currency'
// Per-language key: a currency chosen on RU must never restore on EN (and v.v.).
const storageKeyFor = (isRu) => `${STORAGE_KEY}.${isRu ? 'ru' : 'en'}`

const CurrencyContext = createContext(null)

export function useCurrency() {
    return useContext(CurrencyContext)
}

export function CurrencyProvider({ lang = 'ru', baseCurrency = 'USD', children }) {
    const isRu = lang === 'ru'
    const base = /^(USD|EUR|RUB|UAH|KZT|BYN|GBP)$/.test(baseCurrency) ? baseCurrency : 'USD'

    const options = useMemo(() => {
        const order = isRu ? [...RU_ORDER] : [...EN_ORDER]
        if (!order.includes(base)) order.unshift(base)
        return order
    }, [isRu, base])

    // SSR-stable default; localStorage override is applied after mount so the
    // server and first client render agree (no hydration mismatch).
    // RU → RUB (+ CIS switcher); EN → GBP (never RUB).
    const fallback = isRu ? 'RUB' : 'GBP'
    const [display, setDisplay] = useState(fallback)
    const [rates, setRates] = useState(null) // { USD: 1, EUR: 0.92, ... }

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKeyFor(isRu))
            if (saved && options.includes(saved)) setDisplay(saved)
        } catch { /* private mode / blocked storage */ }
    }, [options, isRu])

    const setDisplayCurrency = useCallback((c) => {
        setDisplay(c)
        try { localStorage.setItem(storageKeyFor(isRu), c) } catch { /* ignore */ }
    }, [isRu])

    useEffect(() => {
        let alive = true
        fetch(`/api/currency-rates?base=USD&symbols=${FETCH_SYMBOLS.join(',')}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d?.rates) setRates({ USD: 1, ...d.rates }) })
            .catch(() => { /* tooltip simply stays hidden */ })
        return () => { alive = false }
    }, [])

    const convert = useCallback((amount, from) => {
        if (!rates || !Number.isFinite(amount)) return null
        const f = rates[from]
        const t = rates[display]
        if (!f || !t) return null
        return (amount * t) / f
    }, [rates, display])

    const value = useMemo(() => ({
        isRu, display, options, setDisplayCurrency, convert,
    }), [isRu, display, options, setDisplayCurrency, convert])

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}

const fmtConverted = (v, currency, isRu) =>
    NO_DECIMALS.has(currency)
        ? Math.round(v).toLocaleString(isRu ? 'ru-RU' : 'en-US')
        : v.toFixed(2)

/**
 * Native price plus an «≈ converted» line in the display currency (when it differs).
 * `className` is applied to the outer span so it can slot into a grid cell
 * (e.g. the ledger's `.p` / `.per` cells) unchanged.
 */
export function ConvMoney({ amount, currency, suffix = '', className = '' }) {
    const ctx = useCurrency()
    const num = Number(amount)
    const native = `${curSym(currency)}${num.toFixed(2)}${suffix}`

    const converted = ctx && ctx.display !== currency ? ctx.convert(num, currency) : null
    if (converted == null) {
        return <span className={className}>{native}</span>
    }

    // Пересчёт строкой под ценой, а не тултипом: на телефоне (74% трафика) наведения нет, и
    // выбранная валюта RUB не давала ни одной цены в рублях (обход 01.10.2026, решение владельца).
    return (
        <span className={className}>
            <span className="cprice">{native}</span>
            <span className="cprice-alt">≈ {curSym(ctx.display)}{fmtConverted(converted, ctx.display, ctx.isRu)}{suffix}</span>
        </span>
    )
}

/** Compact dropdown to pick the display (conversion) currency. */
export function CurrencySwitcher({ className = '' }) {
    const ctx = useCurrency()
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        if (!open) return
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('mousedown', onDoc)
        document.addEventListener('keydown', onKey)
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
    }, [open])

    if (!ctx) return null
    const { display, options, setDisplayCurrency, isRu } = ctx
    const names = CUR_NAME[isRu ? 'ru' : 'en']

    return (
        <div className={`gf-cur ${className}`.trim()} ref={ref}>
            <span className="gf-cur-lbl">{isRu ? 'Валюта' : 'Currency'}</span>
            <button
                type="button"
                className="gf-cur-btn"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                <b>{curSym(display).trim()}</b> {display} <i aria-hidden="true">▾</i>
            </button>
            {open && (
                <ul className="gf-cur-menu" role="listbox" aria-label={isRu ? 'Выбор валюты' : 'Select currency'}>
                    {options.map((c) => (
                        <li key={c} role="option" aria-selected={c === display}>
                            <button
                                type="button"
                                className={c === display ? 'on' : ''}
                                onClick={() => { setDisplayCurrency(c); setOpen(false) }}
                            >
                                <b>{curSym(c).trim()}</b> {c} <span className="nm">{names[c] || ''}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
